import React from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import ExpertiseImage from "../../assets/ExpertiseOne.jpg";
import {
  FaCheck,
  FaBuilding,
  FaGlobe,
  FaCertificate,
  FaTools,
  FaLightbulb,
  FaHandshake,
  FaCalendarAlt,
} from "react-icons/fa";
import { MdFactory, MdEngineering, MdVerified } from "react-icons/md";
import { GiProcessor, GiGears } from "react-icons/gi";

const About = () => {
  return (
    <>
      <Helmet>
        <title>
          Medichy | About Us - Medical Equipment Manufacturer Since 2012
        </title>
        <meta
          name="description"
          content="Medichy is a leading medical equipment manufacturer since 2012. We specialize in custom medical device manufacturing, OEM services, and refurbished equipment with global operations in USA and China."
        />
      </Helmet>
      <AboutPage>
        <div className="about-hero-section">
          <img
            src={ExpertiseImage}
            alt="Medichy Medical Equipment Manufacturing"
          />
          <div className="overlay"></div>
          <div className="hero-content">
            <h1>About Medichy</h1>
            <p>
              Global Leader in Medical Equipment Manufacturing & Custom Device
              Solutions Since 2012
            </p>
          </div>
        </div>

        <div className="container">
          {/* Company Overview */}
          <div className="company-overview">
            <div className="overview-content">
              <h2>Who We Are</h2>
              <p>
                Medichy is a premier medical equipment manufacturer and solution
                provider, dedicated to advancing healthcare through innovative
                medical devices and manufacturing excellence. Since our founding
                in 2012, we have grown into a trusted partner for hospitals,
                clinics, medical device startups, and healthcare organizations
                worldwide.
              </p>
              <p>
                With our state-of-the-art manufacturing facility in China and
                our global headquarters in the USA, we combine Eastern
                manufacturing efficiency with Western quality standards to
                deliver exceptional medical equipment that meets the highest
                international regulations.
              </p>
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-number">18+</div>
                  <div className="stat-label">Years of Excellence</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">50+</div>
                  <div className="stat-label">Countries Served</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">500+</div>
                  <div className="stat-label">Projects Completed</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">100%</div>
                  <div className="stat-label">Client Satisfaction</div>
                </div>
              </div>
            </div>
          </div>

          {/* What We Do */}
          <div className="what-we-do">
            <div className="section-header">
              <h2>What We Do</h2>
              <p>
                Comprehensive medical equipment solutions tailored to your needs
              </p>
            </div>
            <div className="services-grid">
              <div className="service-card">
                <div className="service-icon">
                  <MdFactory />
                </div>
                <h3>Custom Manufacturing</h3>
                <p>
                  Build medical machines and equipment according to client
                  specifications and designs
                </p>
              </div>
              <div className="service-card">
                <div className="service-icon">
                  <FaLightbulb />
                </div>
                <h3>Startup Support</h3>
                <p>
                  Help medical device startups bring their designs to life with
                  our manufacturing expertise
                </p>
              </div>
              <div className="service-card">
                <div className="service-icon">
                  <FaTools />
                </div>
                <h3>Refurbished Equipment</h3>
                <p>
                  High-quality refurbished medical devices at competitive prices
                </p>
              </div>
              <div className="service-card">
                <div className="service-icon">
                  <GiProcessor />
                </div>
                <h3>OEM Manufacturing</h3>
                <p>
                  End-to-end manufacturing solutions for medical device
                  companies
                </p>
              </div>
              <div className="service-card">
                <div className="service-icon">
                  <MdVerified />
                </div>
                <h3>Licensed Production</h3>
                <p>
                  Fully licensed and certified medical equipment manufacturing
                </p>
              </div>
              <div className="service-card">
                <div className="service-icon">
                  <GiGears />
                </div>
                <h3>Design Collaboration</h3>
                <p>
                  Work with clients to build products from their custom designs
                </p>
              </div>
            </div>
          </div>

          {/* Global Presence */}
          <div className="global-presence">
            <div className="section-header">
              <h2>Global Presence</h2>
              <p>Strategic operations spanning two continents</p>
            </div>
            <div className="locations-grid">
              <div className="location-card usa">
                <div className="location-icon">
                  <FaGlobe />
                </div>
                <h3>United States</h3>
                <p className="location-type">Global Headquarters</p>
                <p className="location-address">
                  1496 McKee & Joanna Crescent Rd
                  <br />
                  Dover, DE 19901
                  <br />
                  USA
                </p>
                <div className="location-features">
                  <span>Sales & Distribution</span>
                  <span>R&D Center</span>
                  <span>Quality Control</span>
                </div>
              </div>
              <div className="location-card china">
                <div className="location-icon">
                  <MdFactory />
                </div>
                <h3>China</h3>
                <p className="location-type">Manufacturing Facility</p>
                <p className="location-address">
                  No. 456 Industrial Park Road
                  <br />
                  Shanghai, 201800
                  <br />
                  China
                </p>
                <div className="location-features">
                  <span>Production Plant</span>
                  <span>Assembly Line</span>
                  <span>Logistics Hub</span>
                </div>
              </div>
            </div>
          </div>

          {/* Our Capabilities */}
          <div className="capabilities">
            <div className="section-header">
              <h2>Our Manufacturing Capabilities</h2>
              <p>State-of-the-art facilities and expertise</p>
            </div>
            <div className="capabilities-grid">
              <div className="capability-item">
                <FaCheck className="capability-icon" />
                <span>MRI Systems</span>
              </div>
              <div className="capability-item">
                <FaCheck className="capability-icon" />
                <span>CT Scanners</span>
              </div>
              <div className="capability-item">
                <FaCheck className="capability-icon" />
                <span>X-Ray Equipment</span>
              </div>
              <div className="capability-item">
                <FaCheck className="capability-icon" />
                <span>Ultrasound Machines</span>
              </div>
              <div className="capability-item">
                <FaCheck className="capability-icon" />
                <span>LINAC Systems</span>
              </div>
              <div className="capability-item">
                <FaCheck className="capability-icon" />
                <span>Custom Medical Devices</span>
              </div>
              <div className="capability-item">
                <FaCheck className="capability-icon" />
                <span>Refurbished Equipment</span>
              </div>
              <div className="capability-item">
                <FaCheck className="capability-icon" />
                <span>OEM Manufacturing</span>
              </div>
            </div>
          </div>

          {/* Certifications */}
          <div className="certifications">
            <div className="section-header">
              <h2>Certifications & Compliance</h2>
              <p>Committed to the highest quality standards</p>
            </div>
            <div className="cert-grid">
              <div className="cert-card">
                <FaCertificate />
                <h4>ISO 13485:2016</h4>
                <p>Medical Devices Quality Management</p>
              </div>
              <div className="cert-card">
                <FaCertificate />
                <h4>FDA Registered</h4>
                <p>US Food and Drug Administration</p>
              </div>
              <div className="cert-card">
                <FaCertificate />
                <h4>CE Marking</h4>
                <p>European Conformity</p>
              </div>
              <div className="cert-card">
                <FaCertificate />
                <h4>GMP Certified</h4>
                <p>Good Manufacturing Practice</p>
              </div>
            </div>
          </div>

          {/* Why Choose Us */}
          <div className="why-choose-us">
            <div className="section-header">
              <h2>Why Choose Medichy</h2>
              <p>Partner with a trusted industry leader</p>
            </div>
            <div className="reasons-grid">
              <div className="reason-card">
                <div className="reason-icon">
                  <FaCalendarAlt />
                </div>
                <h4>Since 2012</h4>
                <p>18+ years of industry experience and expertise</p>
              </div>
              <div className="reason-card">
                <div className="reason-icon">
                  <FaHandshake />
                </div>
                <h4>Client-First Approach</h4>
                <p>Work directly with your designs and specifications</p>
              </div>
              <div className="reason-card">
                <div className="reason-icon">
                  <FaBuilding />
                </div>
                <h4>Global Operations</h4>
                <p>USA headquarters & China manufacturing facility</p>
              </div>
              <div className="reason-card">
                <div className="reason-icon">
                  <MdEngineering />
                </div>
                <h4>Expert Team</h4>
                <p>Skilled engineers and technicians</p>
              </div>
            </div>
          </div>

          {/* Contact CTA */}
          <div className="contact-cta">
            <div className="cta-content">
              <h3>Ready to Start Your Medical Equipment Project?</h3>
              <p>
                Whether you need custom manufacturing, startup support, or
                refurbished equipment, our team is ready to help.
              </p>
              <div className="cta-buttons">
                <a href="/contact-us" className="btn-primary">
                  Contact Sales
                </a>
                <a href="/contact-us" className="btn-secondary">
                  Request Quote
                </a>
              </div>
            </div>
          </div>
        </div>
      </AboutPage>
    </>
  );
};

const AboutPage = styled.div`
  width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
  background: #f8fafc;

  .about-hero-section {
    position: relative;
    height: 50vh;
    width: 100%;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }

    .overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        135deg,
        rgba(0, 100, 200, 0.85) 0%,
        rgba(0, 50, 150, 0.9) 100%
      );
    }

    .hero-content {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
      color: #fff;
      width: 90%;
      max-width: 900px;
      padding: 0 1rem;

      h1 {
        font-size: 3.5rem;
        font-weight: 700;
        margin-bottom: 1rem;
        text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3);
        line-height: 1.2;
      }

      p {
        font-size: 1.25rem;
        font-weight: 400;
        opacity: 0.95;
        text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
        line-height: 1.4;
        max-width: 700px;
        margin-left: auto;
        margin-right: auto;
      }
    }
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
  }

  .section-header {
    text-align: center;
    margin-bottom: 3rem;

    h2 {
      font-size: 2.5rem;
      font-weight: 700;
      color: #1a1a1a;
      margin-bottom: 1rem;
      position: relative;
      display: inline-block;

      &::after {
        content: "";
        position: absolute;
        bottom: -12px;
        left: 50%;
        transform: translateX(-50%);
        width: 60px;
        height: 3px;
        background: linear-gradient(135deg, #3182ce, #2c5282);
        border-radius: 2px;
      }
    }

    p {
      font-size: 1.1rem;
      color: #666;
      margin-top: 1.5rem;
      line-height: 1.5;
    }
  }

  .company-overview {
    padding: 4rem 0;
    background: white;
    border-radius: 20px;
    margin: 3rem 0;
    padding: 3rem;

    .overview-content {
      h2 {
        font-size: 2rem;
        font-weight: 700;
        color: #1a1a1a;
        margin-bottom: 1.5rem;
      }

      p {
        font-size: 1.1rem;
        color: #4a5568;
        line-height: 1.6;
        margin-bottom: 1.5rem;
      }

      .stats-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 2rem;
        margin-top: 3rem;
        padding-top: 2rem;
        border-top: 1px solid #e2e8f0;

        .stat-item {
          text-align: center;

          .stat-number {
            font-size: 2.5rem;
            font-weight: 700;
            color: #3182ce;
            margin-bottom: 0.5rem;
          }

          .stat-label {
            font-size: 0.9rem;
            color: #666;
          }
        }
      }
    }
  }

  .what-we-do {
    padding: 4rem 0;

    .services-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 2rem;

      .service-card {
        background: white;
        padding: 2rem;
        border-radius: 16px;
        text-align: center;
        transition: all 0.3s ease;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);

        &:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }

        .service-icon {
          width: 70px;
          height: 70px;
          background: linear-gradient(135deg, #3182ce, #2c5282);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;

          svg {
            font-size: 2rem;
            color: white;
          }
        }

        h3 {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1a1a1a;
          margin-bottom: 1rem;
        }

        p {
          font-size: 0.95rem;
          color: #666;
          line-height: 1.5;
        }
      }
    }
  }

  .global-presence {
    background: white;
    border-radius: 20px;
    padding: 4rem;
    margin: 3rem 0;

    .locations-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 2rem;

      .location-card {
        padding: 2rem;
        border-radius: 16px;
        text-align: center;
        transition: all 0.3s ease;

        &.usa {
          background: linear-gradient(135deg, #ebf8ff, #eef2ff);
          border: 1px solid #3182ce;
        }

        &.china {
          background: linear-gradient(135deg, #fef3c7, #fffbeb);
          border: 1px solid #d97706;
        }

        .location-icon {
          width: 60px;
          height: 60px;
          background: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;

          svg {
            font-size: 1.75rem;
            color: #3182ce;
          }
        }

        h3 {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
        }

        .location-type {
          font-size: 0.9rem;
          font-weight: 600;
          color: #3182ce;
          margin-bottom: 1rem;
        }

        .location-address {
          font-size: 0.95rem;
          color: #4a5568;
          line-height: 1.6;
          margin-bottom: 1.5rem;
        }

        .location-features {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          justify-content: center;

          span {
            background: rgba(255, 255, 255, 0.8);
            padding: 0.3rem 0.8rem;
            border-radius: 20px;
            font-size: 0.8rem;
            font-weight: 500;
            color: #1a1a1a;
          }
        }
      }
    }
  }

  .capabilities {
    padding: 4rem 0;

    .capabilities-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 1rem;

      .capability-item {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 1rem;
        background: white;
        border-radius: 8px;
        transition: all 0.3s ease;

        &:hover {
          background: #edf2f7;
          transform: translateX(5px);
        }

        .capability-icon {
          color: #3182ce;
          font-size: 1rem;
          flex-shrink: 0;
        }

        span {
          font-size: 0.95rem;
          color: #4a5568;
        }
      }
    }
  }

  .certifications {
    background: white;
    border-radius: 20px;
    padding: 4rem;
    margin: 3rem 0;

    .cert-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 2rem;

      .cert-card {
        text-align: center;
        padding: 1.5rem;
        background: #f8fafc;
        border-radius: 12px;
        transition: all 0.3s ease;

        &:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
        }

        svg {
          font-size: 2rem;
          color: #3182ce;
          margin-bottom: 1rem;
        }

        h4 {
          font-size: 1.1rem;
          font-weight: 600;
          color: #1a1a1a;
          margin-bottom: 0.5rem;
        }

        p {
          font-size: 0.85rem;
          color: #666;
        }
      }
    }
  }

  .why-choose-us {
    padding: 4rem 0;

    .reasons-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 2rem;

      .reason-card {
        text-align: center;
        padding: 2rem;
        background: white;
        border-radius: 16px;
        transition: all 0.3s ease;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);

        &:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        }

        .reason-icon {
          width: 60px;
          height: 60px;
          background: linear-gradient(135deg, #3182ce, #2c5282);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;

          svg {
            font-size: 1.5rem;
            color: white;
          }
        }

        h4 {
          font-size: 1.1rem;
          font-weight: 600;
          color: #1a1a1a;
          margin-bottom: 0.5rem;
        }

        p {
          font-size: 0.9rem;
          color: #666;
          line-height: 1.4;
        }
      }
    }
  }

  .contact-cta {
    background: linear-gradient(135deg, #3182ce 0%, #2c5282 100%);
    border-radius: 20px;
    padding: 4rem;
    text-align: center;
    color: white;
    margin: 3rem 0 5rem;

    .cta-content {
      max-width: 600px;
      margin: 0 auto;

      h3 {
        font-size: 2rem;
        font-weight: 700;
        margin-bottom: 1rem;
      }

      p {
        font-size: 1.1rem;
        opacity: 0.95;
        margin-bottom: 2rem;
      }

      .cta-buttons {
        display: flex;
        gap: 1rem;
        justify-content: center;

        .btn-primary {
          background: white;
          color: #3182ce;
          border: none;
          padding: 0.875rem 2rem;
          font-size: 1rem;
          font-weight: 600;
          border-radius: 8px;
          cursor: pointer;
          text-decoration: none;
          transition: all 0.3s ease;

          &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 15px rgba(255, 255, 255, 0.3);
          }
        }

        .btn-secondary {
          background: transparent;
          color: white;
          border: 2px solid white;
          padding: 0.875rem 2rem;
          font-size: 1rem;
          font-weight: 600;
          border-radius: 8px;
          cursor: pointer;
          text-decoration: none;
          transition: all 0.3s ease;

          &:hover {
            background: white;
            color: #3182ce;
            transform: translateY(-2px);
          }
        }
      }
    }
  }

  /* Media Queries */
  @media screen and (max-width: 1366px) {
    .container {
      padding: 0 1.5rem;
    }
  }

  @media screen and (max-width: 1200px) {
    .what-we-do .services-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .capabilities .capabilities-grid {
      grid-template-columns: repeat(3, 1fr);
    }

    .certifications .cert-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .why-choose-us .reasons-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media screen and (max-width: 900px) {
    .about-hero-section .hero-content {
      h1 {
        font-size: 2.5rem;
      }

      p {
        font-size: 1rem;
      }
    }

    .company-overview {
      padding: 2rem;

      .overview-content {
        .stats-grid {
          grid-template-columns: repeat(2, 1fr);
          gap: 1.5rem;
        }
      }
    }

    .global-presence {
      padding: 2rem;

      .locations-grid {
        grid-template-columns: 1fr;
        gap: 1.5rem;
      }
    }

    .capabilities .capabilities-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .contact-cta {
      padding: 2rem;

      .cta-content {
        h3 {
          font-size: 1.5rem;
        }
      }
    }
  }

  @media screen and (max-width: 600px) {
    .about-hero-section {
      height: 40vh;

      .hero-content {
        h1 {
          font-size: 2rem;
        }

        p {
          font-size: 0.9rem;
        }
      }
    }

    .section-header {
      h2 {
        font-size: 1.75rem;
      }

      p {
        font-size: 1rem;
      }
    }

    .what-we-do .services-grid {
      grid-template-columns: 1fr;
    }

    .capabilities .capabilities-grid {
      grid-template-columns: 1fr;
    }

    .certifications .cert-grid {
      grid-template-columns: 1fr;
    }

    .why-choose-us .reasons-grid {
      grid-template-columns: 1fr;
    }

    .contact-cta {
      .cta-content {
        .cta-buttons {
          flex-direction: column;
          gap: 1rem;

          .btn-primary,
          .btn-secondary {
            width: 100%;
          }
        }
      }
    }

    .company-overview {
      .overview-content {
        .stats-grid {
          grid-template-columns: 1fr;
        }
      }
    }
  }

  @media screen and (max-width: 420px) {
    .about-hero-section .hero-content {
      h1 {
        font-size: 1.5rem;
      }
    }

    .section-header {
      h2 {
        font-size: 1.5rem;
      }
    }
  }

  @media screen and (max-width: 350px) {
    .about-hero-section {
      height: 35vh;
    }
  }
`;

export default About;
